import {
    Box,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
} from "@mui/material"
import { useEffect, useRef, useState } from "react"
import DebugService from "../services/DebugService"
import { LoadingButton } from "@mui/lab"
import {
    CONST_SITE_LABEL,
    STORAGE_KEYS,
    setScopedItem
} from "./common/Helper"
import SnackbarNotification, { SNACKBAR_THEME } from "./common/SnackbarNotification"
import ConnectionApiService from "../services/ConnectionApiService"
import { MdOutlineTextSnippet } from "react-icons/md";
import AnsiToHtml from "ansi-to-html";
import { useAuth } from "../state/AuthContext"
import { useScopedLocalStorage } from "../hooks/useScopedLocalStorage"

const ansiConverter = new AnsiToHtml();

/**
 * Execute Custom Commands component
 * @returns 
 */
export const ExecuteCustomCommands = () => {
    const [data, setData] = useState('')
    const [connectionList, setConnectionList] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState(null)
    const [snackbarStatus, setSnackbarStatus] = useState(null)
    const [currentUserAccess, setCurrentUserAccess] = useState(null)
    const [serviceForm, setServiceForm] = useState({
        command: '',
        server: '',
    })
    const [executeCommandLogsApiData, setExecuteCommandLogsApiData] = useState([])
    const [connectionListApiData, setConnectionListApiData] = useState([])
    const logContainerRef = useRef(null);
    const { selectedAccount } = useAuth()
    let fetchLogsInterval = useRef(null)
    const { getScopedItem } = useScopedLocalStorage()

    const defaultLogsText = 'Fetching Logs <span class="dots-animation"></span>'

    /**
     * Fetch connection list
     */
    const fetchConnectList = () => {
        setIsLoading(true)

        ConnectionApiService.getData(selectedAccount)
            .then(res => {
                DebugService.log(`Manage Service Component: API response`, res)
                setConnectionListApiData(res)
                const apiData = (res['data'] ?? []).filter(c => c['isActive'] === 1)
                setConnectionList(apiData)

                if (res['error']) {
                    DebugService.error(`Manage Service Component: Error caught while fetching data`, res['error'])
                }

                setIsLoading(false)
            })
            .catch(err => {
                DebugService.error(`Manage Service Component: Error caught while fetching data`, err)
                setIsLoading(false)
            })
    }

    /**
     * Fetch user data
     */
    const fetchLogs = (jobID) => {
        setIsLoading(true)

        ConnectionApiService.getExecuteCommandLogs(selectedAccount, jobID)
            .then(res => {
                DebugService.log(`Execute Custom Commands Component: API response for job id ${jobID}`, res)
                setExecuteCommandLogsApiData(res)
                const apiData = res['data'] ?? {}

                if (apiData['log'] && apiData['log']['content'] && apiData['log']['content'] !== '') {
                    setData(ansiConverter.toHtml(apiData['log']['content'].replace(/\n/g, "<br>")))
                }

                if (res['error']) {
                    DebugService.error(`Execute Custom Commands Component: Error caught while fetching data`, res['error'])
                }

                setIsLoading(false)
            })
            .catch(err => {
                DebugService.error(`Execute Custom Commands Component: Error caught while fetching data`, err)
                setIsLoading(false)
            })
    }

    /**
     * Call useEffect on page load
     */
    useEffect(() => {
        document.title = `${CONST_SITE_LABEL} | Execute Custom Commands`

        const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
        const parseToJson = JSON.parse(userDetails)
        setCurrentUserAccess(parseToJson['roleName'])
        fetchConnectList()

        // const interval = setInterval(() => {
        //   const newLog = `Log [${new Date().toLocaleTimeString()}]: ${
        //     Math.random() > 0.5 ? "Operation Successful ✅" : "Error Occurred ❌"
        //   }`;
        //   setLogs((prevLogs) => [...prevLogs, newLog]);

        //   // Auto-scroll to bottom
        //   setTimeout(() => {
        //     if (logContainerRef.current) {
        //       logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
        //     }
        //   }, 100);
        // }, 2000);

        // return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        fetchConnectList()
    }, [selectedAccount])

    useEffect(() => {
        if (data !== '' && data !== 'FETCHING_LOGS' && fetchLogsInterval.current) {
            clearInterval(fetchLogsInterval.current)
            setServiceForm({
                command: '',
                server: '',
            })
            fetchLogsInterval.current = null
        }
    }, [data]);

    /**
    * @description Stores the latest API response in storage.
    * @author Ashish Sharma
    */
    useEffect(() => {
        setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA,{
            executeCommandLogsApiData: executeCommandLogsApiData,
            connectionListApiData: connectionListApiData
        })
    },[executeCommandLogsApiData, connectionListApiData]);

    /**
     * API call handler
     * @param {*} endpoint 
     * @param {*} requestBody 
     */
    const saveAPICallHandler = (requestBody) => {
        setSnackbarStatus(null)
        setSnackbarMessage(null)
        DebugService.log(`Execute Custom Commands Component: Request to execute command initiated`, requestBody)

        ConnectionApiService.executeCommand(requestBody)
            .then(res => {
                DebugService.log(`Execute Custom Commands Component: API response`, res)

                if (res['data']) {
                    setSnackbarStatus('success')
                    setSnackbarMessage("Execution Initiated Successfully. Do not navigate from the page until deployment is in progress.")
                    setData('FETCHING_LOGS')
                    setTimeout(() => {
                        fetchLogs(res['data']['jobId'] ?? 0)
                    }, 1000)

                    fetchLogsInterval.current = setInterval(() => {
                        fetchLogs(res['data']['jobId'] ?? 0)
                    }, 20000);
                }

                if (res['error']) {
                    setSnackbarStatus('failed')
                    setSnackbarMessage(res['error']['message'])
                }

                setIsLoading(false)
            })
            .catch(err => {
                DebugService.log(`Execute Custom Commands Component: Failed to execute`, err)
                setIsLoading(false)
                setSnackbarStatus('failed')
                setSnackbarMessage('Something went wrong. Please try later')
            })
    }

    /**
     * Add new connection handler
     * @param {*} formDetails 
     */
    const onSaveClickHandler = () => {
        DebugService.log(`Execute Custom Commands Component: Form Data`, serviceForm)
        setIsLoading(true)
        setSnackbarMessage(null)
        setSnackbarStatus(null)

        const requestBody = {
            command: serviceForm['command'],
            connectionId: serviceForm['server'],
        }

        if (selectedAccount) {
            requestBody['userAccountId'] = selectedAccount
        }

        saveAPICallHandler(requestBody)
    }

    return (
        <>
            {/* <Grid container className="gradient-box"></Grid> */}
            <Grid container spacing={1} className="execute-custom-command-page">
                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        fullWidth
                        size="small"
                        id="outlined-basic"
                        label="Command/ Script"
                        variant="outlined"
                        onChange={(e) =>
                            setServiceForm((prev) => {
                                return {
                                    ...prev,
                                    command: e["target"]["value"],
                                };
                            })
                        }
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <FormControl
                        fullWidth
                        size="small"
                        required
                    // error={errorMessages && errorMessages['infraType'] !== undefined}
                    >
                        <InputLabel id="demo-simple-select-label">Server</InputLabel>
                        <Select
                            labelid="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Server"
                            // multiple
                            onChange={(e) =>
                                setServiceForm((prev) => {
                                    return {
                                        ...prev,
                                        server: e["target"]["value"],
                                    };
                                })
                            }
                            value={serviceForm["server"]}
                        >
                            {connectionList.length > 0 &&
                                connectionList.map((c) => (
                                    <MenuItem key={c["connectionId"]} value={c["connectionId"]}>
                                        {c["connectionName"]} ({c["hostName"]})
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <LoadingButton
                        fullWidth
                        variant="contained"
                        title="Add New Connection"
                        onClick={() => {
                            onSaveClickHandler();
                        }}
                        className="submit-btn"
                        loading={isLoading}
                        loadingPosition="end"
                        endIcon={<img src={"/execute-btn-icon.svg"} alt="Execute Button" width={'28px'} height={'30px'} />}
                    >
                        Execute
                    </LoadingButton>
                </Grid>

                <Grid item xs={12}>
                    <Paper
                        elevation={3}
                        className="logs-container"
                        ref={logContainerRef}
                    >
                        <pre
                            className="log-pre"
                            dangerouslySetInnerHTML={{
                                __html: data === "FETCHING_LOGS" ? defaultLogsText : data,
                            }}
                        />
                    </Paper>
                </Grid>

                {/* Snackbar Notification */}
                {snackbarStatus && (
                    <SnackbarNotification
                        initialOpen={true}
                        duration={5000}
                        message={snackbarMessage}
                        actionButtonName={"Ok"}
                        theme={
                            snackbarStatus === "success" ? SNACKBAR_THEME.GREEN : SNACKBAR_THEME.RED
                        }
                        yPosition={"top"}
                        xPosition={"center"}
                    />
                )}
            </Grid>
        </>
    )
}